'use client'
import { useEffect, useRef, useState } from 'react'
import Script from 'next/script'

/**
 * --------------------------------------------------------------------------
 * CmsEmbed
 *
 * Safely render CMS-provided embed code that includes external and inline scripts.
 * External scripts load first; inline scripts execute after they finish.
 *
 * @param {string} html - The raw HTML embed code from CMS
 * --------------------------------------------------------------------------
 **/
export default function ParseHtmlClient({ html }) {
  const containerRef = useRef(null)
  const [externalScripts, setExternalScripts] = useState([])

  useEffect(() => {
    if (!html) return

    // Temporary container to parse HTML
    const temp = document.createElement('div')
    temp.innerHTML = html

    // Extract external scripts
    const external = Array.from(temp.querySelectorAll('script[src]')).map((s) =>
      s.getAttribute('src'),
    )

    // Remove them from the DOM so only inline + HTML remain
    external.forEach(() => {
      const node = temp.querySelector('script[src]')
      if (node) node.remove()
    })

    // Save the remaining HTML for rendering
    containerRef.current.innerHTML = temp.innerHTML

    // Store unique external script URLs
    setExternalScripts([...new Set(external)])
  }, [html])

  // If there are no external scripts, run inline scripts immediately
  useEffect(() => {
    if (externalScripts.length === 0) runInlineScripts()
  }, [externalScripts])

  // After scripts have loaded, re-execute inline scripts
  const runInlineScripts = () => {
    const scripts = containerRef.current.querySelectorAll('script:not([src])')
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script')
      newScript.innerHTML = oldScript.innerHTML
      oldScript.parentNode.replaceChild(newScript, oldScript)
    })
  }

  return (
    <>
      {/* Render all external scripts first */}
      {externalScripts.map((src, i) => (
        <Script key={i} src={src} strategy="afterInteractive" onLoad={runInlineScripts} />
      ))}

      {/* Then render the rest of the content */}
      <div className="test" ref={containerRef} />
    </>
  )
}
